import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { type Locale, useLocale, useViewMessages } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useSize } from './hooks';
import { DEPENDENTS, JOB_BY_ID, LEVEL_RANK, STAGE_BY_ID, STAGE_COLOR, TREES } from './layout';
import { BUILD_ORDER, HUB, JOBS, type Level, MAP_AS_OF, STAGES, type SystemId } from './nonoMap';
import { GlyphDot, SkyMap, type SkyMessages } from './SkyMap';

/*
 * Nono-kartet — the member journey as a living map of jobs (see nonoMap.ts for
 * the data, layout.ts for the geometry, SkyMap.tsx for the canvas). This page
 * owns the state (layout, which stage is open, selection, level filter) and the
 * plain-HTML parts: headline, legend, detail card, build order and stage list.
 */

type Mode = 'wheel' | 'line';

const LEVELS: Level[] = ['auto', 'assisted', 'manual', 'missing'];
const LEVEL_TONE: Record<Level, 'good' | 'brand' | 'warn' | 'bad'> = {
  auto: 'good',
  assisted: 'brand',
  manual: 'warn',
  missing: 'bad',
};
/** Stage progress bar segments — the same ink ladder as the map glyphs. */
const BAR: Record<Level, string> = {
  auto: 'bg-fg',
  assisted: 'bg-fg/55',
  manual: 'bg-fg/25',
  missing: 'bg-bad/55',
};
const COPPER = '#c58b5f';
const PANEL_PX = 344; // floating detail card (w-80) + its inset
/** The card only floats when the map keeps at least this much width beside it. */
const FLOAT_MIN_MAP_PX = 760;

if (import.meta.env.DEV) {
  for (const j of JOBS) {
    for (const d of j.deps)
      if (!JOB_BY_ID.has(d)) console.warn(`[kart] ${j.id} → unknown dep ${d}`);
    if (!STAGE_BY_ID.has(j.stage)) console.warn(`[kart] ${j.id} → unknown stage ${j.stage}`);
    if ((j.level === 'manual' || j.level === 'missing') && !j.next)
      console.warn(`[kart] ${j.id} → ${j.level} job has no next step`);
  }
}

/* ── copy ─────────────────────────────────────────────────────────────── */

interface KartMessages extends SkyMessages {
  title: string;
  lead: (auto: number, total: number, missing: number, where: string) => string;
  and: string;
  details: string;
  sub: (date: string) => string;
  levelHint: Record<Level, string>;
  system: Record<SystemId, string>;
  filterHint: string;
  clearFilter: string;
  today: string;
  next: string;
  systems: string;
  buildsOn: string;
  hubOnly: string;
  unlocks: string;
  nothing: string;
  evidence: string;
  hubReads: string;
  close: string;
  buildTitle: string;
  buildSub: string;
  stagesTitle: string;
}

const MESSAGES: Record<Locale, KartMessages> = {
  nb: {
    title: 'Nono-kartet · Sosiale medier',
    lead: (auto, total, missing, where) =>
      `Én avdeling om gangen. ${auto} av ${total} jobber i sosiale medier går av seg selv. ${missing} gjøres ikke av noen ennå – flest i ${where}.`,
    and: ' og ',
    details: 'Detaljer',
    sub: (date) =>
      `Kartlagt fra koden ${date} – et øyeblikksbilde, ikke live. Hver gren er en ekte avhengighet: en jobb som trenger en annen jobbs resultat.`,
    level: {
      auto: 'Går av seg selv',
      assisted: 'System + menneske',
      manual: 'Manuelt',
      missing: 'Finnes ikke',
    },
    levelHint: {
      auto: 'Kjører uten at noen gjør noe; et menneske kontrollerer.',
      assisted: 'Systemet forbereder, en navngitt person bestemmer eller sender.',
      manual: 'Noen gjør det for hånd – eller må huske å se etter.',
      missing: 'Ingen gjør dette i dag.',
    },
    system: {
      momence: 'Momence',
      meta: 'Meta',
      shopify: 'Shopify',
      stripe: 'Stripe',
      railway: 'Nono-backend',
      ads: 'ads.nono.no',
      slack: 'Slack',
      email: 'E-post',
      clarity: 'Clarity',
      staff: 'Vertskapet',
      claude: 'Claude',
      library: 'Bildebiblioteket',
    },
    wheel: 'Hjul',
    journey: 'Linje',
    back: 'Kartet',
    zoomIn: 'Zoom inn',
    zoomOut: 'Zoom ut',
    fit: 'Vis hele kartet',
    prevStage: 'Forrige steg',
    nextStage: 'Neste steg',
    skyHint: 'Dra for å snu hjulet · ⌘/Ctrl + rull for å zoome · trykk på et steg for å gå inn',
    skyHintLine: 'Dra for å flytte · ⌘/Ctrl + rull for å zoome · trykk på et steg for å gå inn',
    skyHintTouch: 'Sveip sidelengs for å snu hjulet · knip for å zoome · trykk på et steg',
    diveHint: 'Trykk på en jobb for detaljer · ‹ › bytter steg · Esc tilbake til kartet',
    diveHintTouch: 'Trykk på en jobb for detaljer · «Kartet» øverst tar deg tilbake',
    hubName: 'Innholdshjernen',
    openStage: (name, jobs, gaps) => `${name}: ${jobs}, ${gaps}. Åpne steget.`,
    buildFirst: 'BYGG FØRST',
    filterHint: 'Trykk på et nivå for å se bare de jobbene.',
    clearFilter: 'Vis alle',
    mapLabel: 'Kart over jobbene i sosiale medier',
    today: 'I dag',
    next: 'Neste steg',
    systems: 'Systemer',
    buildsOn: 'Bygger på',
    hubOnly: 'Leser rett fra innholdshjernen.',
    unlocks: 'Låser opp',
    nothing: 'Ingenting ennå.',
    evidence: 'Bevis i koden',
    hubReads: 'Leser rett herfra',
    close: 'Lukk',
    buildTitle: 'Byggerekkefølge',
    buildSub: 'De fem jobbene som gir mest for minst – i den rekkefølgen de bør bygges.',
    stagesTitle: 'Alle jobber, steg for steg',
    jobsWord: (n) => `${n} ${n === 1 ? 'jobb' : 'jobber'}`,
    gapsWord: (n) => `${n} hull`,
    stageOf: (i, n) => `steg ${i} av ${n}`,
  },
  en: {
    title: 'The Nono map · Social media',
    lead: (auto, total, missing, where) =>
      `One department at a time. ${auto} of ${total} social-media jobs run by themselves. ${missing} are done by nobody yet — most of them in ${where}.`,
    and: ' and ',
    details: 'Details',
    sub: (date) =>
      `Mapped from the code on ${date} — a snapshot, not live. Every branch is a real dependency: a job that needs another job’s output.`,
    level: {
      auto: 'Runs by itself',
      assisted: 'System + human',
      manual: 'Manual',
      missing: 'Missing',
    },
    levelHint: {
      auto: 'Runs without anyone doing anything; a human audits.',
      assisted: 'The system prepares, a named person decides or sends.',
      manual: 'Someone does it by hand — or must remember to look.',
      missing: 'Nobody does this today.',
    },
    system: {
      momence: 'Momence',
      meta: 'Meta',
      shopify: 'Shopify',
      stripe: 'Stripe',
      railway: 'Nono backend',
      ads: 'ads.nono.no',
      slack: 'Slack',
      email: 'Email',
      clarity: 'Clarity',
      staff: 'Hosts',
      claude: 'Claude',
      library: 'Photo library',
    },
    wheel: 'Wheel',
    journey: 'Line',
    back: 'Map',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    fit: 'Show the whole map',
    prevStage: 'Previous stage',
    nextStage: 'Next stage',
    skyHint: 'Drag to turn the wheel · ⌘/Ctrl + scroll to zoom · tap a stage to open it',
    skyHintLine: 'Drag to move · ⌘/Ctrl + scroll to zoom · tap a stage to open it',
    skyHintTouch: 'Swipe sideways to turn the wheel · pinch to zoom · tap a stage',
    diveHint: 'Tap a job for details · ‹ › switches stage · Esc back to the map',
    diveHintTouch: 'Tap a job for details · “Map” at the top takes you back',
    hubName: 'Content brain',
    openStage: (name, jobs, gaps) => `${name}: ${jobs}, ${gaps}. Open the stage.`,
    buildFirst: 'BUILD FIRST',
    filterHint: 'Tap a level to see only those jobs.',
    clearFilter: 'Show all',
    mapLabel: 'Map of the social-media jobs',
    today: 'Today',
    next: 'Next step',
    systems: 'Systems',
    buildsOn: 'Builds on',
    hubOnly: 'Reads straight from the content brain.',
    unlocks: 'Unlocks',
    nothing: 'Nothing yet.',
    evidence: 'Evidence in the code',
    hubReads: 'Reads straight from here',
    close: 'Close',
    buildTitle: 'Build order',
    buildSub: 'The five jobs that give the most for the least — in the order they should be built.',
    stagesTitle: 'Every job, stage by stage',
    jobsWord: (n) => `${n} ${n === 1 ? 'job' : 'jobs'}`,
    gapsWord: (n) => `${n} ${n === 1 ? 'gap' : 'gaps'}`,
    stageOf: (i, n) => `stage ${i} of ${n}`,
  },
};

/* ── page ─────────────────────────────────────────────────────────────── */

export function KartPage() {
  const m = useViewMessages(MESSAGES);
  const { locale } = useLocale();
  const [mode, setModeRaw] = useState<Mode>('wheel');
  const [diveStage, setDiveRaw] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  // A scene change unmounts whatever was hovered without a pointerleave/blur,
  // so every way of switching scene clears the hover with it.
  const setDiveStage = useCallback((id: string | null) => {
    setHovered(null);
    setDiveRaw(id);
  }, []);
  const setMode = useCallback((next: Mode) => {
    setHovered(null);
    setModeRaw(next);
  }, []);
  const [levelFilter, setLevelFilter] = useState<Level | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const card = useSize(mapRef);
  // Float the detail card over the map only when the map keeps a readable width
  // beside it; otherwise (phones, small laptops) it sits in flow under the map.
  const canFloat = card.w - PANEL_PX >= FLOAT_MIN_MAP_PX;
  const floatPanel = Boolean(selected) && canFloat;
  const canFloatRef = useRef(canFloat);
  canFloatRef.current = canFloat;

  const counts = useMemo(() => {
    const c: Record<Level, number> = { auto: 0, assisted: 0, manual: 0, missing: 0 };
    for (const j of JOBS) c[j.level]++;
    return c;
  }, []);
  const where = useMemo(() => {
    const names = STAGES.map((s, i) => ({
      s,
      i,
      n: JOBS.filter((j) => j.stage === s.id && j.level === 'missing').length,
    }))
      .filter((r) => r.n > 0)
      .sort((a, b) => b.n - a.n || a.i - b.i)
      .slice(0, 3)
      .sort((a, b) => a.i - b.i)
      .map((r) => `${locale === 'nb' ? r.s.name.nb.toLowerCase() : r.s.name.en} (${r.n})`);
    const last = names.pop();
    return names.length ? `${names.join(', ')}${m.and}${last}` : (last ?? '');
  }, [locale, m.and]);

  // Keyboard users: a selection made with Enter moves focus into the card, and
  // closing it returns focus to whatever opened it.
  const wantFocusRef = useRef(false);
  const openerRef = useRef<Element | null>(null);
  const prevSelectedRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevSelectedRef.current;
    prevSelectedRef.current = selected;
    const activeEl = document.activeElement;
    const lostFocus = !activeEl || activeEl === document.body;
    if (selected) {
      if (!prev) openerRef.current = activeEl;
      if (wantFocusRef.current || lostFocus || panelRef.current?.contains(activeEl)) {
        requestAnimationFrame(() => titleRef.current?.focus({ preventScroll: true }));
      }
      wantFocusRef.current = false;
      if (!canFloatRef.current)
        panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else if (prev) {
      const opener = openerRef.current;
      openerRef.current = null;
      const canRefocus = opener instanceof HTMLElement || opener instanceof SVGElement;
      if (lostFocus && canRefocus && opener.isConnected) {
        requestAnimationFrame(() => opener.focus({ preventScroll: true }));
      }
    }
  }, [selected]);

  const select = (id: string | null, viaKeyboard = false) => {
    if (viaKeyboard) wantFocusRef.current = true;
    setSelected(id);
  };
  /** From the card: follow a job into its own stage when a stage is open. */
  const selectFromPanel = (id: string | null) => {
    const j = id ? JOB_BY_ID.get(id) : undefined;
    if (j && diveStage && j.stage !== diveStage) setDiveStage(j.stage);
    setSelected(id);
  };
  /** From the lists below: open the job's stage and show the job (focus follows into the card). */
  const focusOnMap = (id: string) => {
    wantFocusRef.current = true;
    const j = JOB_BY_ID.get(id);
    if (j) setDiveStage(j.stage);
    setSelected(id);
    if (canFloat) mapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Esc peels one layer: the card first, then the open stage.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      if (selected) setSelected(null);
      else if (diveStage) setDiveStage(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, diveStage, setDiveStage]);

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-title text-fg">{m.title}</h1>
        <p className="max-w-3xl text-body text-fg">
          {m.lead(counts.auto, JOBS.length, counts.missing, where)}
        </p>
        <p className="max-w-3xl text-label text-fg-subtle">{m.sub(MAP_AS_OF)}</p>
      </div>

      {/* legend = filter */}
      <div className="flex flex-wrap items-center gap-2">
        {LEVELS.map((lv) => {
          const on = levelFilter === lv;
          return (
            <button
              key={lv}
              type="button"
              title={m.levelHint[lv]}
              aria-pressed={on}
              onClick={() => setLevelFilter(on ? null : lv)}
              className={cn(
                'flex h-10 items-center gap-2 rounded-full border px-3 text-label transition-fast sm:h-8',
                on
                  ? 'border-fg/40 bg-surface-3 text-fg'
                  : 'border-border bg-surface text-fg-muted hover:bg-surface-2 hover:text-fg',
                levelFilter && !on && 'opacity-50',
              )}
            >
              <GlyphDot level={lv} className="size-3" />
              {m.level[lv]}
              <span className="tabular font-semibold text-fg">{counts[lv]}</span>
            </button>
          );
        })}
        {levelFilter ? (
          <button
            type="button"
            onClick={() => setLevelFilter(null)}
            className="h-10 px-2 text-label text-fg-subtle underline-offset-2 hover:text-fg hover:underline sm:h-8"
          >
            {m.clearFilter}
          </button>
        ) : (
          <span className="hidden text-label text-fg-subtle sm:inline">{m.filterHint}</span>
        )}
      </div>

      <Card ref={mapRef} className="relative scroll-mt-4 overflow-hidden">
        <SkyMap
          locale={locale}
          m={m}
          mode={mode}
          onMode={setMode}
          diveStage={diveStage}
          onDive={setDiveStage}
          selected={selected}
          hovered={hovered}
          levelFilter={levelFilter}
          panelPx={floatPanel ? PANEL_PX : 0}
          onHover={setHovered}
          onSelect={select}
        />
        {selected && (
          <section
            ref={panelRef}
            aria-label={m.details}
            className={cn(
              'scroll-mt-4 p-4',
              floatPanel
                ? 'absolute inset-y-3 right-3 z-20 w-80 overflow-y-auto rounded-lg border border-border bg-surface shadow-card'
                : 'border-t border-border',
            )}
          >
            <DetailPanel
              id={selected}
              locale={locale}
              m={m}
              titleRef={titleRef}
              onSelect={selectFromPanel}
            />
          </section>
        )}
      </Card>

      <BuildOrder locale={locale} m={m} selected={selected} onSelect={focusOnMap} />
      <StageGrid locale={locale} m={m} selected={selected} onSelect={focusOnMap} />
    </div>
  );
}

/* ── side panel ───────────────────────────────────────────────────────── */

function JobChip({
  id,
  locale,
  onSelect,
}: {
  id: string;
  locale: Locale;
  onSelect: (id: string) => void;
}) {
  const j = JOB_BY_ID.get(id);
  if (!j) return null;
  return (
    <button
      type="button"
      onClick={() => onSelect(id)}
      className="flex min-h-9 items-center gap-1.5 rounded-md border border-border bg-surface-2 px-2 py-1 text-left text-label text-fg transition-fast hover:bg-surface-3 sm:min-h-0"
    >
      <GlyphDot level={j.level} className="size-2.5" />
      {j.name[locale]}
    </button>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-micro font-semibold tracking-wider text-fg-subtle uppercase">
        {title}
      </div>
      {children}
    </div>
  );
}

function DetailPanel({
  id,
  locale,
  m,
  titleRef,
  onSelect,
}: {
  id: string | null;
  locale: Locale;
  m: KartMessages;
  titleRef: RefObject<HTMLDivElement | null>;
  onSelect: (id: string | null) => void;
}) {
  if (!id) return null;

  if (id === 'hub') {
    const direct = JOBS.filter((j) => !j.deps.length);
    return (
      <div className="flex flex-col gap-4">
        <PanelHead
          title={HUB.name[locale]}
          crumb=""
          m={m}
          titleRef={titleRef}
          onClose={() => onSelect(null)}
        />
        <p className="text-body text-fg">{HUB.today[locale]}</p>
        <Section title={m.systems}>
          <SystemChips ids={HUB.systems} m={m} />
        </Section>
        <Section title={m.hubReads}>
          <div className="flex flex-wrap gap-1.5">
            {direct.map((j) => (
              <JobChip key={j.id} id={j.id} locale={locale} onSelect={onSelect} />
            ))}
          </div>
        </Section>
        <Section title={m.evidence}>
          <Evidence paths={HUB.evidence} />
        </Section>
      </div>
    );
  }

  const j = JOB_BY_ID.get(id);
  if (!j) return null;
  const stageIdx = STAGES.findIndex((s) => s.id === j.stage);
  const dependents = DEPENDENTS.get(j.id) ?? [];
  return (
    <div className="flex flex-col gap-4">
      <PanelHead
        title={j.name[locale]}
        crumb={`${STAGE_BY_ID.get(j.stage)?.name[locale] ?? ''} · ${m.stageOf(stageIdx + 1, STAGES.length)}`}
        m={m}
        titleRef={titleRef}
        onClose={() => onSelect(null)}
      />
      <div>
        <Badge tone={LEVEL_TONE[j.level]}>
          <GlyphDot level={j.level} className="size-2" />
          {m.level[j.level]}
        </Badge>
      </div>
      <Section title={m.today}>
        <p className="text-body text-fg">{j.today[locale]}</p>
      </Section>
      {j.next && (
        <div className="rounded-md border border-border bg-surface-2 p-3">
          <div className="mb-1 text-micro font-semibold tracking-wider text-fg-subtle uppercase">
            {m.next}
          </div>
          <p className="text-body text-fg">{j.next[locale]}</p>
        </div>
      )}
      <Section title={m.systems}>
        <SystemChips ids={j.systems} m={m} />
      </Section>
      <Section title={m.buildsOn}>
        {j.deps.length ? (
          <div className="flex flex-wrap gap-1.5">
            {j.deps.map((d) => (
              <JobChip key={d} id={d} locale={locale} onSelect={onSelect} />
            ))}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onSelect('hub')}
            className="w-fit text-left text-label text-fg-muted underline-offset-2 hover:text-fg hover:underline"
          >
            {m.hubOnly}
          </button>
        )}
      </Section>
      <Section title={m.unlocks}>
        {dependents.length ? (
          <div className="flex flex-wrap gap-1.5">
            {dependents.map((d) => (
              <JobChip key={d} id={d} locale={locale} onSelect={onSelect} />
            ))}
          </div>
        ) : (
          <span className="text-label text-fg-subtle">{m.nothing}</span>
        )}
      </Section>
      <Section title={m.evidence}>
        <Evidence paths={j.evidence} />
      </Section>
    </div>
  );
}

function PanelHead({
  title,
  crumb,
  m,
  titleRef,
  onClose,
}: {
  title: string;
  crumb: string;
  m: KartMessages;
  titleRef: RefObject<HTMLDivElement | null>;
  onClose: () => void;
}) {
  return (
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">
        {crumb && <div className="text-micro text-fg-subtle">{crumb}</div>}
        <div ref={titleRef} tabIndex={-1} className="text-title text-fg">
          {title}
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label={m.close}
        className="flex size-9 shrink-0 items-center justify-center rounded-md text-fg-subtle transition-fast hover:bg-surface-2 hover:text-fg sm:size-7"
      >
        ✕
      </button>
    </div>
  );
}

function SystemChips({ ids, m }: { ids: SystemId[]; m: KartMessages }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {ids.map((s) => (
        <Badge key={s} tone="neutral">
          {m.system[s]}
        </Badge>
      ))}
    </div>
  );
}

function Evidence({ paths }: { paths: string[] }) {
  return (
    <ul className="flex flex-col gap-1">
      {paths.map((p) => (
        <li key={p} className="break-all font-mono text-micro text-fg-muted">
          {p}
        </li>
      ))}
    </ul>
  );
}

/* ── below the map ────────────────────────────────────────────────────── */

function BuildOrder({
  locale,
  m,
  selected,
  onSelect,
}: {
  locale: Locale;
  m: KartMessages;
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <Card className="p-4">
      <div className="mb-3 flex flex-col gap-0.5">
        <h2 className="text-section text-fg">{m.buildTitle}</h2>
        <p className="text-label text-fg-subtle">{m.buildSub}</p>
      </div>
      <ol className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-5">
        {BUILD_ORDER.map((id, i) => {
          const j = JOB_BY_ID.get(id);
          if (!j) return null;
          return (
            <li key={id}>
              <button
                type="button"
                onClick={() => onSelect(id)}
                className={cn(
                  'flex h-full w-full flex-col gap-1.5 rounded-md border p-3 text-left transition-fast',
                  selected === id
                    ? 'border-fg/30 bg-surface-3'
                    : 'border-border bg-surface-2 hover:bg-surface-3',
                )}
              >
                <div className="flex items-center gap-2">
                  <span
                    className="flex size-5 shrink-0 items-center justify-center rounded-full text-micro font-bold"
                    style={{ background: COPPER, color: '#0b0e14' }}
                  >
                    {i + 1}
                  </span>
                  <span className="text-micro text-fg-subtle">
                    {STAGE_BY_ID.get(j.stage)?.name[locale]}
                  </span>
                </div>
                <span className="text-body font-medium text-fg">{j.name[locale]}</span>
                {j.next && <span className="text-label text-fg-muted">{j.next[locale]}</span>}
              </button>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

function StageGrid({
  locale,
  m,
  selected,
  onSelect,
}: {
  locale: Locale;
  m: KartMessages;
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-section text-fg">{m.stagesTitle}</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {TREES.map((t) => {
          const c = t.counts;
          const jobs = [...t.nodes.values()]
            .map((n) => n.job)
            .sort((a, b) => LEVEL_RANK[a.level] - LEVEL_RANK[b.level]);
          return (
            <Card key={t.stage.id} className="flex flex-col gap-2 p-3">
              <div className="flex items-baseline justify-between gap-2">
                <span className="flex items-center gap-1.5 text-label font-semibold text-fg">
                  <span
                    aria-hidden
                    className="size-2 shrink-0 rounded-full ring-1 ring-fg/25"
                    style={{ background: STAGE_COLOR[t.stage.id] }}
                  />
                  {t.index + 1}. {t.stage.name[locale]}
                </span>
                <span className="shrink-0 text-micro text-fg-subtle">
                  {m.jobsWord(jobs.length)}
                </span>
              </div>
              <div className="flex h-1.5 overflow-hidden rounded-full bg-surface-3">
                {LEVELS.map((lv) =>
                  c[lv] ? (
                    <span
                      key={lv}
                      className={BAR[lv]}
                      style={{ width: `${(c[lv] / jobs.length) * 100}%` }}
                    />
                  ) : null,
                )}
              </div>
              <ul className="flex flex-col">
                {jobs.map((j) => (
                  <li key={j.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(j.id)}
                      className={cn(
                        'flex min-h-9 w-full items-center gap-2 rounded px-1.5 py-1 text-left text-label transition-fast sm:min-h-0',
                        selected === j.id
                          ? 'bg-surface-3 text-fg'
                          : 'text-fg-muted hover:bg-surface-2 hover:text-fg',
                      )}
                    >
                      <GlyphDot level={j.level} className="size-2.5" />
                      <span className="truncate" title={j.name[locale]}>
                        {j.name[locale]}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
